import { useState, type FormEvent } from "react";
import { CalendarX, MapPin, UserCircleCheck, WarningCircle } from "@phosphor-icons/react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Banner } from "@/components/Banner";
import { Button } from "@/components/Button";
import { GuestFooter } from "@/components/GuestFooter";
import { Input, Select } from "@/components/Input";
import { Skeleton } from "@/components/Skeleton";
import { useApiErrorToast } from "@/components/Toast";
import { ItemsSection } from "@/components/event/ItemsSection";
import { ParticipantsBar } from "@/components/event/ParticipantsBar";
import { ParticipantsList } from "@/components/event/ParticipantsList";
import { usePageMeta } from "@/hooks/usePageMeta";
import { usePublicEvent, useSelfRegister } from "@/hooks/useJoinEvent";
import { useRecentEvents } from "@/hooks/useRecentEvents";
import { formatEventDate, formatRelative } from "@/lib/dates";
import styles from "./JoinEvent.module.css";

export function JoinEvent() {
  const params = useParams();
  const shortcut = params.shortcut ?? "";
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: event, isLoading, error, refetch } = usePublicEvent(shortcut);
  const register = useSelfRegister(shortcut);
  const { entries, add } = useRecentEvents();
  const apiError = useApiErrorToast();
  const [name, setName] = useState("");
  const [persons, setPersons] = useState(1);

  usePageMeta({
    title: event?.name ?? t("join.heading"),
    robots: "noindex,nofollow",
  });

  if (isLoading) return <Skeleton variant="form" />;

  if (error || !event) {
    const status = (error as { status?: number })?.status;
    if (status === 404) {
      return (
        <section className={styles.page}>
          <h1>{t("join.notFoundTitle")}</h1>
          <p>{t("join.notFoundBody")}</p>
        </section>
      );
    }
    return (
      <section className={styles.page}>
        <Banner variant="warning" icon={WarningCircle} title={t("join.errorTitle")}>
          {t("join.errorBody")}
        </Banner>
        <Button type="button" onClick={() => void refetch()}>
          {t("join.retry")}
        </Button>
      </section>
    );
  }

  const locale = i18n.language || "de";
  const canceled = event.canceledAt !== null;
  const isFull =
    event.participantsMax !== null && event.participantsUsed >= event.participantsMax;
  const knownEntry = entries.find((e) => e.shortcut === shortcut && e.role === "user");
  const hasPreview = event.participants.length > 0 || event.items.length > 0;

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      const participant = await register.mutateAsync({ name: name.trim(), persons });
      add({
        shortcut: event.shortcut,
        key: participant.userKey,
        role: "user",
        name: event.name,
        eventAt: event.eventAt,
      });
      navigate(`/view/${event.shortcut}/${participant.userKey}`, { replace: true });
    } catch (err) {
      apiError(err, t("common.errorGeneric"));
    }
  };

  return (
    <section>
      <p className={styles.kicker}>{t("join.heading")}</p>
      <h1 className={styles.title}>{event.name}</h1>
      <div className={styles.meta}>
        <span>{formatEventDate(event.eventAt, event.timezone, locale)}</span>
        <span>{formatRelative(event.eventAt, locale)}</span>
        {event.location && (
          <span className={styles.location}>
            <MapPin size={14} weight="regular" /> {event.location}
          </span>
        )}
        {event.participantsMax !== null && !canceled && (
          <span className={isFull ? styles.capacityFull : styles.capacity}>
            {t("join.capacity", {
              used: event.participantsUsed,
              max: event.participantsMax,
            })}
          </span>
        )}
        {canceled && (
          <span className={styles.canceledChip}>
            <CalendarX size={14} weight="regular" /> {t("event.canceled")}
          </span>
        )}
      </div>
      {event.description && <p className={styles.description}>{event.description}</p>}

      {knownEntry && (
        <div className={styles.knownBox}>
          <Banner
            variant="success"
            icon={UserCircleCheck}
            title={t("join.alreadyJoinedTitle")}
          >
            {t("join.alreadyJoinedBody")}{" "}
            <Link to={`/view/${shortcut}/${knownEntry.key}`}>
              {t("join.alreadyJoinedCta")}
            </Link>
          </Banner>
        </div>
      )}

      {canceled ? (
        <Banner variant="danger" icon={CalendarX} title={t("join.canceled")} />
      ) : !event.expandable ? (
        <Banner variant="info" icon={WarningCircle} title={t("join.notFoundTitle")}>
          {t("join.notExpandableLead")}
        </Banner>
      ) : isFull ? (
        <Banner variant="warning" icon={WarningCircle} title={t("join.capacityFull")} />
      ) : (
        !knownEntry && (
          <>
            <p className={styles.lead}>{t("join.lead")}</p>
            <form onSubmit={onSubmit} className={styles.form}>
              <Input
                label={t("join.nameLabel")}
                placeholder={t("join.namePlaceholder")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                maxLength={50}
                required
                autoFocus
              />
              <Select
                label={t("join.personsLabel")}
                value={persons}
                onChange={(e) => setPersons(Number(e.target.value))}
              >
                {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Select>
              <div className={styles.submitRow}>
                <Button type="submit" disabled={register.isPending || !name.trim()}>
                  {register.isPending ? t("join.submitting") : t("join.submit")}
                </Button>
              </div>
              {event.participants.length > 0 && (
                <p className={styles.duplicateHint}>{t("join.duplicateHint")}</p>
              )}
            </form>
          </>
        )
      )}

      {hasPreview && (
        <div className={styles.preview}>
          <ParticipantsBar
            participants={event.participants}
            detailsSlot={
              <ParticipantsList viewerRole="guest" participants={event.participants} />
            }
          />
          {event.items.length > 0 && (
            <ItemsSection
              viewerRole="guest"
              items={event.items}
              participants={event.participants}
            />
          )}
        </div>
      )}
      <GuestFooter />
    </section>
  );
}
